import { levelMeta } from "@/lib/levels";
import { motion } from "framer-motion";
import { Trophy, Star, Lock, ShieldCheck } from "@/lib/icons";
import { Shield } from "lucide-react";

export function ShieldCount({ shields, max = 2 }) {
  return (
    <div className="text-center" title="Streak Shields: earn one a month; use it to protect a missed weekly goal" data-testid="shield-count">
      <div className="flex items-center justify-center gap-1 text-emerald-700">
        {Array.from({ length: max }).map((_, i) => (
          <Shield key={`shield-${i + 1}`} className={`w-5 h-5 ${i < shields ? "fill-emerald-500 text-emerald-700" : "text-stone-300"}`} />
        ))}
      </div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mt-0.5">shields</div>
    </div>
  );
}

export function ResilienceCount({ count }) {
  if (!(count > 0)) return null;
  return (
    <div className="text-center" title="Hard days you still showed up — check-ins made while your wellbeing was trending down" data-testid="resilience-count">
      <div className="flex items-center justify-center gap-1 text-amber-700">
        <ShieldCheck className="w-5 h-5 fill-amber-200" />
        <span className="font-mono text-2xl font-bold text-amber-800">{count}</span>
      </div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mt-0.5">hard days, still showed up</div>
    </div>
  );
}

export function LevelHeader({ level, levels = [] }) {
  const { Icon, badge } = levelMeta(level.name);
  return (
    <div className="flex items-center gap-4">
      <div className={`w-14 h-14 rounded-2xl ${badge} ring-4 ring-white shadow-md flex items-center justify-center`} data-testid="level-badge">
        <Icon className="w-7 h-7" />
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Level {level.index}</div>
        <div className="font-display text-2xl font-bold text-stone-900" data-testid="level-name">{level.name}</div>
        {levels.length > 0 && (
          <div className="flex gap-1 mt-1" data-testid="level-path">
            {levels.map((l, i) => { const m = levelMeta(l.name); const reached = i < level.index; return <span key={l.name} title={`${l.name} · ${l.min} pts`} className={`w-5 h-5 rounded-full grid place-items-center ${reached ? m.badge : "bg-stone-100 text-stone-300"}`}><m.Icon className="w-3 h-3" /></span>; })}
          </div>
        )}
      </div>
    </div>
  );
}

export function XpBar({ level, nextText }) {
  return (
    <div>
      <div className="h-3 rounded-full bg-stone-100 overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-emerald-500" initial={{ width: 0 }}
          animate={{ width: `${Math.max(3, level.progress * 100)}%` }} transition={{ duration: 0.8, ease: "easeOut" }} data-testid="xp-bar" />
      </div>
      <div className="text-xs text-stone-500 mt-1.5 font-mono">{nextText}</div>
    </div>
  );
}

function AchievementTile({ a }) {
  return (
    <div title={a.description} data-testid={`achievement-${a.key}`}
      className={`rounded-2xl p-3 text-center border transition ${a.unlocked ? "bg-amber-50 border-amber-200 text-stone-900" : "bg-stone-50 border-stone-100 text-stone-400"}`}>
      {a.unlocked ? <Star className="w-5 h-5 mx-auto text-amber-600 fill-amber-400" /> : <Lock className="w-5 h-5 mx-auto" />}
      <div className="text-[11px] font-semibold mt-1.5 leading-tight">{a.title}</div>
      <div className="text-[10px] font-mono mt-0.5">+{a.points}</div>
    </div>
  );
}

export function AchievementGrid({ catalog, unlockedCount }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-amber-700" />
        <span className="text-xs font-bold uppercase tracking-widest text-stone-700">Achievements · {unlockedCount}/{catalog.length}</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {catalog.map((a) => <AchievementTile key={a.key} a={a} />)}
      </div>
    </div>
  );
}
